/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Backward-compatible retry wrapper.
 * Delegates to retryWithBackoff (the canonical implementation).
 */

import {
  retryWithBackoff,
  type RetryConfig as BackoffRetryConfig,
} from "./retry-with-backoff";

export {
  retryWithBackoff,
  RETRY_CONFIGS,
  isRetryableError,
} from "./retry-with-backoff";
export type { RetryConfig as BackoffRetryConfig } from "./retry-with-backoff";

export interface RetryConfig {
  maxAttempts?: number;
  baseDelay?: number;
  maxDelay?: number;
  exponentialBackoff?: boolean;
  jitter?: boolean;
  retryableErrors?: (error: Error | unknown) => boolean;
}

/**
 * Conservative retryable error detection (original withRetry behavior).
 * Unlike isRetryableError from retry-with-backoff which defaults to retry,
 * this defaults to NOT retry for unrecognized errors.
 */
function defaultRetryableError(error: Error): boolean {
  const message = error.message.toLowerCase();

  // Don't retry 4xx errors (except 429)
  if (
    message.includes("400") ||
    message.includes("401") ||
    message.includes("403") ||
    message.includes("404")
  ) {
    return false;
  }

  // Retry 429 (rate limit) and 5xx errors
  if (
    message.includes("429") ||
    message.includes("500") ||
    message.includes("502") ||
    message.includes("503") ||
    message.includes("504")
  ) {
    return true;
  }

  // Retry network errors
  const name = error.name.toLowerCase();
  if (
    name === "networkerror" ||
    name === "timeouterror" ||
    name === "aborterror" ||
    message.includes("network") ||
    message.includes("timeout") ||
    message.includes("aborted") ||
    message.includes("fetch failed")
  ) {
    return true;
  }

  return false;
}

export async function withRetry<T>(
  fn: () => Promise<T>,
  config: RetryConfig = {},
): Promise<T> {
  const {
    maxAttempts = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    exponentialBackoff = true,
    jitter = true,
    retryableErrors,
  } = config;

  const backoffConfig: BackoffRetryConfig = {
    maxAttempts,
    baseDelay,
    maxDelay,
    backoffMultiplier: exponentialBackoff ? 2 : 1,
    jitter,
    retryableErrors: retryableErrors
      ? (err: Error) => retryableErrors(err)
      : defaultRetryableError,
  };

  return retryWithBackoff(fn, backoffConfig);
}
